import React, { createContext, useContext, useEffect, useRef, useState, lazy, Suspense } from "react";
import { ArrowUpRight, ArrowLeft } from "lucide-react";
import { useLang, translations } from "@/i18n";
import { LOCATIONS } from "@/lib/locations";
import { RESERVATION_VENUES } from "@/lib/reservationConfig";
import { HomeDialog } from "./HomeDialog";
import { HOME_COPY as C } from "@/data/homeContent";

const BookingContext = createContext(null);
const ReservationWidget = lazy(() => import("@/components/ReservationWidget").then((module) => ({ default: module.ReservationWidget })));
export const useHomeBooking = () => useContext(BookingContext);
const CM_LANGUAGE = { es: "spanish", en: "english", va: "valencian" };

export const HomeBookingProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const opener = useRef(null);
  const { t, lang } = useLang();
  const venue = LOCATIONS.find((v) => v.slug === selected);
  const url = venue?.reservation.mode === "covermanager" ? `https://www.covermanager.com/reserve/module_restaurant/${venue.reservation.coverManagerSlug}/${CM_LANGUAGE[lang]}` : null;
  const openBooking = (slug, trigger) => { opener.current = trigger || document.activeElement; setSelected(slug || null); setOpen(true); };
  return <BookingContext.Provider value={{ openBooking, open }}>
    {children}
    <HomeDialog open={open} onOpenChange={setOpen} id="home-booking-dialog" title={venue ? venue.name : t(C.bookingPick)} description={t(C.bookingDescription)} className="rh-booking-dialog" onCloseAutoFocus={(event) => { event.preventDefault(); opener.current?.focus({ preventScroll: true }); }}>
      {venue ? <div className="rh-booking-content">
        <button data-testid="home-booking-back" className="rh-text-link" onClick={() => setSelected(null)}><ArrowLeft size={15} />{t(C.bookingBack)}</button>
        {venue.reservation.mode === "own" ? <div className="rh-own-booking"><Suspense fallback={<p className="rh-body" role="status" data-testid="home-booking-loading">{t(C.bookingLoading)}</p>}><ReservationWidget venue={RESERVATION_VENUES[venue.slug]} /></Suspense></div> : <>
          <a href={url} target="_blank" rel="noopener noreferrer" className="rh-booking-external" data-testid="home-booking-external">{t(C.bookingExternal)}<ArrowUpRight size={15} /></a>
          <iframe key={url} src={url} title={`${t(translations.nav.reservar)} · ${venue.name}`} className="rh-booking-iframe" data-testid={`home-covermanager-${venue.slug}`} loading="lazy" allow="payment" referrerPolicy="strict-origin-when-cross-origin" />
        </>}
      </div> : <div className="rh-booking-venues">
        {LOCATIONS.map((loc, index) => <button key={loc.slug} data-testid={`home-booking-select-${loc.slug}`} onClick={() => setSelected(loc.slug)}>
          <span className="rh-label">0{index + 1}</span><span>{loc.shortName}</span><ArrowUpRight size={23} strokeWidth={1.2} />
        </button>)}
      </div>}
    </HomeDialog>
  </BookingContext.Provider>;
};

export const BookButton = ({ children, venue, testId, className = "" }) => {
  const { openBooking } = useContext(BookingContext);
  const { t } = useLang();
  return <button type="button" onClick={(e) => openBooking(venue, e.currentTarget)} className={`rh-book-button ${className}`} data-testid={testId}>{children || t(translations.nav.reservar)}<ArrowUpRight size={15} strokeWidth={1.4} aria-hidden="true" /></button>;
};

export const MobileBookBar = () => {
  const { open } = useContext(BookingContext);
  const slot = useRef(null);
  const [docked, setDocked] = useState(false);
  useEffect(() => {
    // One button: fixed while reading, in-flow at the existing closing CTA.
    // The permanent slot keeps layout stable when the booking dialog opens.
    let frame = null;
    const sync = () => {
      frame = null;
      setDocked(slot.current.getBoundingClientRect().bottom <= window.innerHeight);
    };
    const schedule = () => { if (frame === null) frame = window.requestAnimationFrame(sync); };
    // A jump can move the slot from below to above without ever intersecting.
    // Scroll/pageshow cover that case; observers cover late layout/size changes.
    const observer = new IntersectionObserver(schedule, { threshold: [0, 1] });
    observer.observe(slot.current);
    const resize = new ResizeObserver(schedule);
    resize.observe(slot.current);
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    window.addEventListener("pageshow", schedule);
    sync();
    return () => {
      observer.disconnect(); resize.disconnect();
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      window.removeEventListener("pageshow", schedule);
      if (frame !== null) window.cancelAnimationFrame(frame);
    };
  }, []);
  return <div ref={slot} className="rh-mobile-book-slot" data-testid="home-mobile-book-slot">
    <div className={`rh-mobile-book-bar ${docked ? "is-docked" : ""}`} data-testid="home-mobile-book-bar" data-mode={docked ? "inline" : "fixed"} hidden={open}>
      <BookButton testId="mobile-sticky-reservar-button" />
    </div>
  </div>;
};